import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, User, Edit2, Plus, X, Phone, ChevronDown, Car, Building, CreditCard, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import Breadcrumb from '../shared/Breadcrumb';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import driverApi from '../../api/driverApi';
import vehicalsManagementApi, { getVehicleTrips } from '../../api/vehicalsManagementApi';
// import { getSstFormatByVehicleAndDate } from '../../api/sstFormatapi';
import { getAllClients } from '../../api/createClientApi';
import VehicleDetailsModal from './VehicleDetailsModal';
import CustomSelect from '../shared/CustomSelect';
import { toast } from '../shared/Toast';
import { showConfirm } from '../shared/ConfirmContext';
import { VEHICLE_TYPES } from '../../constants/vehicleConstants';

const DriverDetail = ({ driver, vehicle, onBack }) => {

  const [showDriverModal, setShowDriverModal] = useState(false);
  const [, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [, setShowAddDriverForm] = useState(false);
  const [driverForm, setDriverForm] = useState({
    driverName: '',
    address: '',
    contactNumber: '',
    aadharNumber: '',
    panNumber: '',
    licenseNumber: '',
    aadharFront: null,
    aadharBack: null,
    panDoc: null,
    licenseFront: null,
    licenseBack: null,
    pvcDocument: null,
    bgvDocument: null,
    passport: null,
    medicalCertificate: null
  });
  const [linkedDriver, setLinkedDriver] = useState(null);
  const [vehicleDrivers, setVehicleDrivers] = useState([]);
  const [showEditDropdown, setShowEditDropdown] = useState(false);
  const [showUpdateVehicleModal, setShowUpdateVehicleModal] = useState(false);
  const [showVehicleDetailsModal, setShowVehicleDetailsModal] = useState(false);
  const [showUpdateDriverModal, setShowUpdateDriverModal] = useState(false);
  const [showChangeCompanyModal, setShowChangeCompanyModal] = useState(false);
  const [showUpdateBankModal, setShowUpdateBankModal] = useState(false);
  const [vehicleData, setVehicleData] = useState(driver || vehicle);
  const [travelStats, setTravelStats] = useState(null);
  const [travelStatsLoading, setTravelStatsLoading] = useState(false);
  const [travelStatsError, setTravelStatsError] = useState('');
  const multiMonthStats = useMemo(() => {
    if (!travelStats?.stats?.monthlyStats) return [];

    return travelStats.stats.monthlyStats.map(m => {
      const [yearStr, monthStr] = m.month.split('-');
      const year = parseInt(yearStr);
      const monthIndex = parseInt(monthStr) - 1;
      const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthIndex] || 'Jan';

      return {
        month: monthName,
        year: year,
        totalKms: parseFloat(m.totalKms) || 0,
        totalTrips: parseInt(m.totalTrips) || 0,
        earnings: parseFloat(m.earnings) || 0
      };
    });
  }, [travelStats]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonthName, setSelectedMonthName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [chartWindowStart, setChartWindowStart] = useState(6); // Start index for 6-month window (showing months 6-11 initially)
  // const [allTrips, setAllTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [allVehicleTrips, setAllVehicleTrips] = useState([]);


  // Compute derived vehicle data
  const d = useMemo(() => vehicleData || driver || vehicle || {
    ownerName: 'Unknown Owner',
    vehicleNumber: 'N/A',
    vehicleType: 'N/A',
    clientName: 'N/A',
    contactNumber: '',
    phone: '',
    paymentMode: 'N/A',
    rate: 'N/A'
  }, [vehicleData, driver, vehicle]);

  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [clientNames, setClientNames] = useState([]);
  const [vehicleForm, setVehicleForm] = useState({
    vehicleNumber: (driver || vehicle)?.vehicleNumber || '',
    vehicleType: (driver || vehicle)?.vehicleType || '',
    ownerName: (driver || vehicle)?.ownerName || '',
    contactNumber: (driver || vehicle)?.contactNumber || (driver || vehicle)?.phone || ''
  });
  const [updateDriverForm, setUpdateDriverForm] = useState({
    driverName: '',
    contactNumber: '',
    address: '',
    aadharNumber: '',
    panNumber: '',
    licenseNumber: '',
    // File uploads
    aadharFront: null,
    aadharBack: null,
    panDoc: null,
    licenseFront: null,
    licenseBack: null,
    // NEW Mandatory Documents

    pvcDocument: null,
    bgvDocument: null,
    passport: null,
    medicalCertificate: null
  });
  const [originalDriverForm, setOriginalDriverForm] = useState({});


  // Bank details form state
  const [bankDetailsForm, setBankDetailsForm] = useState({
    accountHolderName: '',
    accountNumber: '',
    bankName: '',
    bankCategory: 'APW',
    ifscCode: '',
    branch: ''
  });
  const [existingBankDetails, setExistingBankDetails] = useState(null);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);

  // Fetch current assignments for vehicle
  useEffect(() => {
    const fetchCurrentAssignments = async () => {
      if (!driver && !vehicle) return;

      try {
        const vehicleId = driver?.id || vehicle?.id;
        const assignmentsResponse = await vehicalsManagementApi.getAllVehicleClientAssignments();
        const assignments = assignmentsResponse.data || [];
        const vehicleAssignments = assignments.filter(a =>
          (a.vehicleId || a.vehicle_id) === vehicleId || (a.vehicle?.id) === vehicleId
        );

        // Fetch all clients to create a client map for name resolution
        const clientsResponse = await getAllClients(1, 1000);
        const clientsArray = clientsResponse.clients || clientsResponse.data || [];
        const clientMap = {};
        clientsArray.forEach(client => {
          const clientId = client.id;
          const clientName = client.clientName || client.client_name || client.companyName || client.company_name || client.name || 'Unknown';
          if (clientId) {
            clientMap[clientId] = clientName;
          }
        });

        // Enhance assignments with client names
        const enhancedAssignments = vehicleAssignments.map(assignment => {
          const clientId = assignment.clientId || assignment.client_id || assignment.client?.id || assignment.Client?.id;
          let clientName = assignment.client?.clientName
            || assignment.client?.name
            || assignment.client?.companyName
            || assignment.Client?.clientName
            || assignment.Client?.name
            || assignment.Client?.companyName
            || assignment.clientName
            || assignment.client_name;

          // Fallback: lookup clientName from clientMap using clientId
          if (!clientName && clientId && clientMap[clientId]) {
            clientName = clientMap[clientId];
          }

          return {
            ...assignment,
            clientName: clientName || 'Unknown Client'
          };
        });

        setCurrentAssignments(enhancedAssignments);

        // Extract unique client names for display
        const uniqueClientNames = [...new Set(enhancedAssignments.map(a => a.clientName).filter(name => name && name !== 'Unknown Client'))];
        setClientNames(uniqueClientNames);

      } catch (err) {
        setCurrentAssignments([]);
        setClientNames([]);
      }
    };

    fetchCurrentAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.id, vehicle?.id]);

  // Fetch clients when Change Company modal opens
  useEffect(() => {
    if (showChangeCompanyModal) {
      const fetchClients = async () => {
        try {
          const response = await getAllClients(1, 100);
          const clientsArray = response.clients || response.data || [];
          setClients(clientsArray.map(c => ({
            id: c.id,
            companyName: c.clientName || c.client_name || c.company_name || c.companyName || c.name || 'Unknown'
          })));
        } catch (err) {
          // Error fetching clients
        }
      };
      fetchClients();
    }
  }, [showChangeCompanyModal]);

  // Fetch bank details when modal opens
  useEffect(() => {
    if (showUpdateBankModal) {
      const fetchBankDetails = async () => {
        try {
          setBankDetailsLoading(true);
          const vehicleId = vehicle?.id || driver?.id;
          if (!vehicleId) {
            setBankDetailsLoading(false);
            return;
          }

          const response = await vehicalsManagementApi.getAccountDetails(vehicleId);

          if (response.success && response.data) {
            const bankData = response.data;
            setExistingBankDetails(bankData);
            setBankDetailsForm({
              accountHolderName: bankData.accountHolderName || bankData.holderName || bankData.name || vehicleData?.ownerName || '',
              accountNumber: bankData.accountNumber || '',
              bankName: bankData.bankName || '',
              bankCategory: bankData.bankCategory || 'APW',
              ifscCode: bankData.ifscCode || '',
              branch: bankData.branch || ''
            });
          } else {
            setExistingBankDetails(null);
            setBankDetailsForm({
              accountHolderName: vehicleData?.ownerName || '',
              accountNumber: '',
              bankName: '',
              bankCategory: 'APW',
              ifscCode: '',
              branch: ''
            });
          }
        } catch (err) {
          setExistingBankDetails(null);
          setBankDetailsForm({
            accountHolderName: vehicleData?.ownerName || '',
            accountNumber: '',
            bankName: '',
            bankCategory: 'APW',
            ifscCode: '',
            branch: ''
          });
        } finally {
          setBankDetailsLoading(false);
        }
      };
      fetchBankDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpdateBankModal, driver?.id, vehicle?.id]); // Do NOT include vehicleData — it mutates inside this effect and causes infinite loop
  useEffect(() => {
    const fetchLinkedDriver = async () => {
      const vehicleId = driver?.id || vehicle?.id;
      if (!vehicleId) return;

      try {
        const response = await driverApi.getAllDrivers();
        if (response.success && response.data) {
          // Find all drivers assigned to this vehicle in the backend
          const backendDrivers = response.data.filter(d =>
            d.vehicleId === vehicleId || d.vehicle_id === vehicleId
          );

          // Only update if we have backend drivers and no existing UI drivers
          // This preserves manually added drivers
          if (backendDrivers.length > 0 && vehicleDrivers.length === 0) {
            setVehicleDrivers(backendDrivers);
            setLinkedDriver(backendDrivers[0]);
          } else if (backendDrivers.length === 0 && vehicleDrivers.length === 0) {
            // No drivers anywhere
            setVehicleDrivers([]);
            setLinkedDriver(null);
          }
          // If we already have UI drivers, keep them as they might include manually added ones
        }
      } catch (err) {
        console.error('Error fetching drivers:', err);
        // Don't clear existing drivers on error
      }
    };

    fetchLinkedDriver();
  }, [driver?.id, vehicle?.id, vehicleDrivers.length]);

  // Refresh data on mount or when vehicle/driver changes
  useEffect(() => {
    const initData = async () => {
      await refreshData();
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.id, vehicle?.id]);

  // Fetch travel stats when vehicle changes or date filters change
  useEffect(() => {
    const fetchTravelStats = async () => {
      const vehicleId = driver?.id || vehicle?.id;
      if (!vehicleId) return;

      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        setTravelStatsError('From Date cannot be later than To Date');
        setTravelStatsLoading(false);
        setAllVehicleTrips([]);
        return;
      }

      setTravelStatsLoading(true);
      setTravelStatsError('');
      try {
        const response = await vehicalsManagementApi.getVehicleTravelStats(
          vehicleId,
          startDate || endDate ? null : selectedMonth,
          startDate || endDate ? null : selectedYear,
          startDate || null,
          endDate || null
        );
        setTravelStats(response.data || response);
      } catch (err) {
        setTravelStatsError('Failed to load travel statistics');
      } finally {
        setTravelStatsLoading(false);
      }
    };

    fetchTravelStats();
  }, [driver?.id, vehicle?.id, selectedMonth, selectedYear, startDate, endDate]);



  // Fetch all trips for the selected month or date range
  useEffect(() => {
    const fetchAllTrips = async () => {
      const vehicleId = driver?.id || vehicle?.id;
      if (!vehicleId) return;

      setTripsLoading(true);
      try {
        if (startDate || endDate) {
          if (travelStats?.recentTrips) {
            setAllVehicleTrips(travelStats.recentTrips);
          }
        } else {
          // Fetch all trips for the selected month with higher limit
          const response = await getVehicleTrips(vehicleId, selectedMonth, selectedYear, 1, 200);
          const trips = response?.data?.trips || response?.trips || [];
          setAllVehicleTrips(trips);
        }
      } catch (err) {
        // Fallback to recentTrips from travelStats if available
        if (travelStats?.recentTrips) {
          setAllVehicleTrips(travelStats.recentTrips);
        }
      } finally {
        setTripsLoading(false);
      }
    };

    fetchAllTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.id, vehicle?.id, selectedMonth, selectedYear, startDate, endDate, travelStats?.recentTrips]);

  const fetchDrivers = async () => {
    try {
      const response = await driverApi.getAllDrivers();
      if (response.success) {
        setDrivers(response.data || []);
      }
    } catch (err) {
      // Error fetching drivers
    }
  };



  // Refresh vehicle data, linked driver, and client assignment
  const refreshData = async () => {
    const vehicleId = driver?.id || vehicle?.id;
    if (!vehicleId) return;

    try {
      // Refresh vehicle data
      const vehicleResponse = await vehicalsManagementApi.getVehicleById(vehicleId);
      let updatedVehicleData = vehicleResponse.data;

      // Fetch client assignment to get current client name
      const assignmentsResponse = await vehicalsManagementApi.getAllVehicleClientAssignments();
      const assignments = assignmentsResponse.data || [];
      const currentAssignment = assignments.find(a =>
        (a.vehicleId || a.vehicle_id) === vehicleId || (a.vehicle?.id) === vehicleId
      );

      // Merge client name into vehicle data
      if (currentAssignment) {
        const clientName = currentAssignment.client?.clientName
          || currentAssignment.client?.name
          || currentAssignment.clientName
          || currentAssignment.client_name
          || 'N/A';
        updatedVehicleData = {
          ...updatedVehicleData,
          clientName: clientName
        };
      } else {
        updatedVehicleData = {
          ...updatedVehicleData,
          clientName: 'N/A'
        };
      }

      // Fetch bank details by vehicle ID
      try {
        const bankResponse = await vehicalsManagementApi.getAccountDetails(vehicleId);
        if (bankResponse.success && bankResponse.data) {
          const bankData = bankResponse.data;
          updatedVehicleData = {
            ...updatedVehicleData,
            accountDetailsId: bankData.id,
            accountHolderName: bankData.accountHolderName || bankData.holderName,
            accountNumber: bankData.accountNumber,
            bankName: bankData.bankName,
            bankCategory: bankData.bankCategory,
            ifscCode: bankData.ifscCode,
            branch: bankData.branch
          };
        }
      } catch (bankErr) {
        // 404 means no bank details yet — that's fine
        if (bankErr.response?.status !== 404) {
          console.error('Error fetching bank details:', bankErr);
        }
      }

      if (vehicleResponse.success && vehicleResponse.data) {
        setVehicleData(updatedVehicleData);
      }

      // Refresh all vehicle drivers (preserve manually added ones)
      const driverResponse = await driverApi.getAllDrivers();
      if (driverResponse.success && driverResponse.data) {
        const backendDrivers = driverResponse.data.filter(d => d.vehicleId === vehicleId || d.vehicle_id === vehicleId);

        // Merge backend drivers with existing UI drivers to preserve manually added ones
        setVehicleDrivers(prev => {
          const merged = [...backendDrivers];

          // Add any UI drivers that aren't in the backend response
          prev.forEach(uiDriver => {
            if (!backendDrivers.find(bd => bd.id === uiDriver.id) && uiDriver.vehicleId === vehicleId) {
              merged.push(uiDriver);
            }
          });

          return merged;
        });

        // Set the first driver as linkedDriver
        setVehicleDrivers(currentDrivers => {
          setLinkedDriver(currentDrivers.length > 0 ? currentDrivers[0] : null);
          return currentDrivers;
        });
      }

      // Refresh current assignments
      const assignmentsRefreshResponse = await vehicalsManagementApi.getAllVehicleClientAssignments();
      const assignmentsRefresh = assignmentsRefreshResponse.data || [];
      const vehicleAssignmentsRefresh = assignmentsRefresh.filter(a =>
        (a.vehicleId || a.vehicle_id) === vehicleId || (a.vehicle?.id) === vehicleId
      );

      // Enhance assignments with client names
      const enhancedAssignmentsRefresh = vehicleAssignmentsRefresh.map(assignment => {
        const clientId = assignment.clientId || assignment.client_id || assignment.client?.id || assignment.Client?.id;
        let clientName = assignment.client?.clientName
          || assignment.client?.name
          || assignment.client?.companyName
          || assignment.Client?.clientName
          || assignment.Client?.name
          || assignment.Client?.companyName
          || assignment.clientName
          || assignment.client_name
          || 'Unknown Client';

        return {
          ...assignment,
          clientName,
          clientId
        };
      });

      setCurrentAssignments(enhancedAssignmentsRefresh);

      // Extract unique client names for display
      const uniqueClientNamesRefresh = [...new Set(enhancedAssignmentsRefresh.map(a => a.clientName).filter(name => name && name !== 'Unknown Client'))];
      setClientNames(uniqueClientNamesRefresh);

    } catch (err) {
      // Error refreshing data
    }
  };

  const handleOpenDriverModal = () => {
    setShowDriverModal(true);
    fetchDrivers();
  };

  const handleDeactivate = async () => {
    const vehicleId = driver?.id || vehicle?.id;
    if (!vehicleId) {
      toast.warning('No vehicle selected');
      return;
    }

    const currentStatus = vehicleData?.status || 'Active';
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    const isDeactivating = currentStatus === 'Active';
    const actionText = isDeactivating ? 'deactivate' : 'activate';

    const confirmed = await showConfirm({
      title: isDeactivating ? 'Deactivate Vehicle' : 'Activate Vehicle',
      message: `Are you sure you want to ${actionText} this vehicle?`,
      danger: isDeactivating,
      type: isDeactivating ? 'danger' : 'success',
      confirmLabel: isDeactivating ? 'Deactivate' : 'Activate',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await vehicalsManagementApi.updateVehicle(vehicleId, { status: newStatus });
      if (response.success) {
        toast.success(`Vehicle ${actionText}d successfully!`);
        // Update local state to reflect the change
        setVehicleData(prev => ({ ...prev, status: newStatus }));
      } else {
        toast.error(response.message || `Failed to ${actionText} vehicle`);
      }
    } catch (err) {
      toast.error(`Error ${actionText}ing vehicle: ` + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };


  const handleAddNewDriver = async (e) => {
    e.preventDefault();

    if (!driverForm.driverName.trim()) {
      toast.warning('Please enter driver name');
      return;
    }
    if (!driverForm.contactNumber || driverForm.contactNumber.length < 10) {
      toast.warning('Please enter a valid 10-digit contact number');
      return;
    }
    // Validate aadhar number has 12 digits
    const aadharDigits = driverForm.aadharNumber.replace(/\D/g, '');
    if (driverForm.aadharNumber && aadharDigits.length !== 12) {
      toast.warning('Please enter a valid 12-digit Aadhar number');
      return;
    }
    // Validate license number format: AA-12-1234-1234567
    if (driverForm.licenseNumber) {
      const licensePattern = /^[A-Z]{2}-\d{2}-\d{4}-\d{7}$/;
      if (!licensePattern.test(driverForm.licenseNumber)) {
        toast.warning('Please enter license number in format: AA-12-1234-1234567');
        return;
      }
    }

    // Validate mandatory documents
    if (!driverForm.pvcDocument) {
      toast.warning('Please upload Police Verification Certificate (PVC)');
      return;
    }
    if (!driverForm.bgvDocument) {
      toast.warning('Please upload Background Verification (BGV) document');
      return;
    }
    if (!driverForm.passport) {
      toast.warning('Please upload Passport document');
      return;
    }

    try {
      setLoading(true);
      const vehicleId = driver?.id || vehicle?.id;

      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('driverName', driverForm.driverName);
      formData.append('contactNumber', driverForm.contactNumber);
      formData.append('address', driverForm.address);
      // Only send aadhar if it has exactly 12 digits (send with spaces)
      const aadharDigits = driverForm.aadharNumber.replace(/\D/g, '');
      if (aadharDigits.length === 12) {
        formData.append('aadharNumber', aadharDigits.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3'));
      }
      formData.append('panNumber', driverForm.panNumber);
      formData.append('licenseNumber', driverForm.licenseNumber);
      formData.append('vehicleId', vehicleId);

      // Append documents if selected (using same field names as backend expects)
      if (driverForm.aadharFront) {
        formData.append('aadharFrontImage', driverForm.aadharFront);
      }
      if (driverForm.aadharBack) {
        formData.append('aadharBackImage', driverForm.aadharBack);
      }
      if (driverForm.panDoc) {
        formData.append('panImage', driverForm.panDoc);
      }
      if (driverForm.licenseFront) {
        formData.append('licenseImage', driverForm.licenseFront);
      }
      if (driverForm.licenseBack) {
        formData.append('licenseBackImage', driverForm.licenseBack);
      }
      if (driverForm.pvcDocument) {
        formData.append('pvcDocument', driverForm.pvcDocument);
      }
      if (driverForm.bgvDocument) {
        formData.append('bgvDocument', driverForm.bgvDocument);
      }
      if (driverForm.passport) {
        formData.append('passport', driverForm.passport);
      }
      if (driverForm.medicalCertificate) {
        formData.append('medicalCertificate', driverForm.medicalCertificate);
      }

      const response = await driverApi.addDriver(formData);

      if (response.success) {
        const driverId = response.data?.id || response.driver?.id || response.id;
        // Documents are uploaded in the same POST request, no separate call needed

        // Try to assign the driver to the vehicle (but don't fail if already assigned)
        if (driverId && vehicleId) {
          try {
            const assignResponse = await driverApi.assignVehicleToDriver(driverId, vehicleId);
            if (!assignResponse.success) {
              // Don't show error - driver will still be displayed in UI
            }
          } catch (vehicleErr) {
            // This is expected if vehicle is already assigned to another driver
            // Don't show error - driver will still be displayed in UI
          }
        }

        // Create the new driver object with vehicle association
        const newDriver = {
          id: driverId,
          driverName: driverForm.driverName,
          contactNumber: driverForm.contactNumber,
          licenseNumber: driverForm.licenseNumber,
          vehicleId: vehicleId,
          // Mark as associated even if backend assignment failed
          isAssociated: true
        };

        // Add to vehicleDrivers array to maintain multiple drivers
        setVehicleDrivers(prev => {
          const updated = [...prev, newDriver];
          return updated;
        });

        // Set as linkedDriver (for backward compatibility)
        setLinkedDriver(newDriver);

        toast.success('Driver added successfully! Multiple drivers are now supported for this vehicle.');
        setShowAddDriverForm(false);
        setDriverForm({
          driverName: '',
          address: '',
          contactNumber: '',
          aadharNumber: '',
          panNumber: '',
          licenseNumber: '',
          aadharFront: null,
          aadharBack: null,
          panDoc: null,
          licenseFront: null,
          licenseBack: null
        });
        setShowDriverModal(false);
        // Don't call refreshData here as it overwrites the linkedDriver we just set
        // The driver is already assigned in the UI
      } else {
        toast.error(response.message || 'Failed to add driver');
      }
    } catch (err) {
      // Show specific error message from backend if available
      const errorMessage = err.response?.data?.message || err.message || 'Error adding driver';
      toast.error('Error: ' + JSON.stringify(err.response?.data || errorMessage));
    } finally {
      setLoading(false);
    }
  };

  // Handler for Update Vehicle
  const handleUpdateVehicle = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const vehicleId = driver?.id || vehicle?.id;
      const response = await vehicalsManagementApi.updateVehicle(vehicleId, vehicleForm);
      if (response.success) {
        toast.success('Vehicle updated successfully!');
        setShowUpdateVehicleModal(false);
        await refreshData();
      } else {
        toast.error(response.message || 'Failed to update vehicle');
      }
    } catch (err) {
      toast.error('Error updating vehicle: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler for Update Driver
  const handleUpdateDriver = async (e) => {
    e.preventDefault();
    if (!linkedDriver) {
      toast.warning('No driver assigned to update');
      return;
    }
    if (!updateDriverForm.driverName.trim()) {
      toast.warning('Please enter driver name');
      return;
    }
    if (!updateDriverForm.contactNumber || updateDriverForm.contactNumber.length < 10) {
      toast.warning('Please enter a valid 10-digit contact number');
      return;
    }
    // Allow any license number format (Indian DL formats vary by state)
    if (updateDriverForm.licenseNumber && updateDriverForm.licenseNumber.trim().length < 5) {
      toast.warning('Please enter a valid license number (minimum 5 characters)');
      return;
    }
    try {
      setLoading(true);

      // Build FormData with only changed fields
      const formData = new FormData();
      formData.append('driverId', linkedDriver.id);

      // Only append fields that have changed
      if (updateDriverForm.driverName !== originalDriverForm.driverName) {
        formData.append('driverName', updateDriverForm.driverName);
      }
      if (updateDriverForm.contactNumber !== originalDriverForm.contactNumber) {
        formData.append('contactNumber', updateDriverForm.contactNumber);
      }
      if (updateDriverForm.address !== originalDriverForm.address) {
        formData.append('address', updateDriverForm.address);
      }
      // Normalize both values (remove all non-digits) for comparison
      const newAadharDigits = updateDriverForm.aadharNumber.replace(/\D/g, '');
      const oldAadharDigits = (originalDriverForm.aadharNumber || '').replace(/\D/g, '');
      if (newAadharDigits !== oldAadharDigits && newAadharDigits.length === 12) {
        formData.append('aadharNumber', newAadharDigits.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3'));
      }
      if (updateDriverForm.panNumber !== originalDriverForm.panNumber) {
        formData.append('panNumber', updateDriverForm.panNumber);
      }
      if (updateDriverForm.licenseNumber !== originalDriverForm.licenseNumber) {
        formData.append('licenseNumber', updateDriverForm.licenseNumber);
      }

      // Append documents if selected (using same field names as backend expects)
      if (updateDriverForm.aadharFront) {
        formData.append('aadharFrontImage', updateDriverForm.aadharFront);
      }
      if (updateDriverForm.aadharBack) {
        formData.append('aadharBackImage', updateDriverForm.aadharBack);
      }
      if (updateDriverForm.panDoc) {
        formData.append('panImage', updateDriverForm.panDoc);
      }
      if (updateDriverForm.licenseFront) {
        formData.append('licenseImage', updateDriverForm.licenseFront);
      }
      if (updateDriverForm.licenseBack) {
        formData.append('licenseBackImage', updateDriverForm.licenseBack);
      }
      if (updateDriverForm.pvcDocument) {
        formData.append('pvcDocument', updateDriverForm.pvcDocument);
      }
      if (updateDriverForm.bgvDocument) {
        formData.append('bgvDocument', updateDriverForm.bgvDocument);
      }
      if (updateDriverForm.passport) {
        formData.append('passport', updateDriverForm.passport);
      }
      if (updateDriverForm.medicalCertificate) {
        formData.append('medicalCertificate', updateDriverForm.medicalCertificate);
      }

      const response = await driverApi.updateDriver(linkedDriver.id, formData);

      if (response.success) {
        toast.success('Driver updated successfully!');
        setShowUpdateDriverModal(false);
        await refreshData();
      } else {
        toast.error(response.message || 'Failed to update driver');
      }
    } catch (err) {
      toast.error('Error updating driver: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler for Change Assigned Company
  const handleChangeCompany = async () => {
    if (selectedClients.length === 0) {
      toast.warning('Please select at least one client');
      return;
    }
    try {
      setLoading(true);
      const vehicleId = driver?.id || vehicle?.id;

      // Get current assignments for this vehicle
      const assignmentsResponse = await vehicalsManagementApi.getAllVehicleClientAssignments();
      const assignments = assignmentsResponse.data || [];
      const vehicleAssignments = assignments.filter(a =>
        (a.vehicleId || a.vehicle_id) === vehicleId || (a.vehicle?.id) === vehicleId
      );

      // Get already assigned client IDs
      const alreadyAssignedClientIds = vehicleAssignments.map(a =>
        a.clientId || a.client_id || a.client?.id || a.Client?.id
      );

      // Find which selected clients are not already assigned
      const newClientsToAssign = selectedClients.filter(client =>
        !alreadyAssignedClientIds.includes(client.id)
      );

      if (newClientsToAssign.length === 0) {
        toast.info('All selected companies are already assigned to this vehicle');
        setLoading(false);
        return;
      }

      // Create new assignments only for the clients that aren't already assigned
      let allSuccessful = true;
      for (const client of newClientsToAssign) {
        const result = await vehicalsManagementApi.assignVehicleToClient(client.id, vehicleId);
        if (!result.success) {
          allSuccessful = false;
        }
      }

      const response = { success: allSuccessful };

      if (response.success) {
        const addedCount = newClientsToAssign.length;
        toast.success(`${addedCount} compan${addedCount > 1 ? 'ies' : 'y'} assigned successfully!`);
        setShowChangeCompanyModal(false);
        setSelectedClients([]);
        await refreshData();
      } else {
        toast.error(response.message || 'Failed to assign companies');
      }
    } catch (err) {
      toast.error('Error changing assigned company: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Handler for Update Bank Details
  const handleUpdateBankDetails = async (e) => {
    e.preventDefault();

    const holderName = (bankDetailsForm.accountHolderName || '').trim();
    const accountNumber = (bankDetailsForm.accountNumber || '').trim();
    const bankName = (bankDetailsForm.bankName || '').trim();
    const bankCategory = bankDetailsForm.bankCategory || 'APW';
    const ifscCode = (bankDetailsForm.ifscCode || '').trim().toUpperCase();
    const branch = (bankDetailsForm.branch || '').trim();

    // Validate Account Holder Name
    if (!holderName) {
      toast.warning('Please enter Account Holder Name');
      return;
    }
    if (holderName.length < 2) {
      toast.warning('Account Holder Name must be at least 2 characters long');
      return;
    }

    // Validate Account Number (numeric, 9 to 18 digits)
    if (!accountNumber) {
      toast.warning('Please enter Account Number');
      return;
    }
    if (!/^\d{9,18}$/.test(accountNumber)) {
      toast.warning('Account Number must be between 9 and 18 digits (numbers only)');
      return;
    }

    // Validate Bank Name
    if (!bankName) {
      toast.warning('Please enter Bank Name');
      return;
    }

    // Validate Bank Category
    if (!['APW', 'APO'].includes(bankCategory)) {
      toast.warning('Please select a valid Bank Category (APW or APO)');
      return;
    }

    // Validate IFSC Code (Indian standard IFSC format: 4 letters, zero, 6 alphanumeric chars)
    if (!ifscCode) {
      toast.warning('Please enter IFSC Code');
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      toast.warning('Invalid IFSC Code format. It must start with 4 letters, followed by 0, and 6 numbers/letters (e.g., SBIN0001234)');
      return;
    }

    // Validate Branch
    if (!branch) {
      toast.warning('Please enter Branch Name');
      return;
    }

    try {
      setBankDetailsLoading(true);
      const vehicleId = vehicle?.id || driver?.vehicleId;

      const payload = { ...bankDetailsForm };

      // Use single upsert endpoint — creates or updates by vehicleId
      const response = await vehicalsManagementApi.saveAccountDetailsByVehicle(vehicleId, payload);

      if (response.success) {
        toast.success(response.message || 'Bank details saved successfully!');
        setShowUpdateBankModal(false);
        // Refresh vehicle data to reflect the change
        const updatedBankData = {
          accountHolderName: bankDetailsForm.accountHolderName,
          accountNumber: bankDetailsForm.accountNumber,
          bankName: bankDetailsForm.bankName,
          bankCategory: bankDetailsForm.bankCategory,
          ifscCode: bankDetailsForm.ifscCode,
          branch: bankDetailsForm.branch,
          accountDetailsId: response.data?.id
        };
        setVehicleData(prev => ({ ...prev, ...updatedBankData }));
        setExistingBankDetails(prev => ({ ...prev, ...updatedBankData, id: response.data?.id }));
      } else {
        toast.error(response.message || 'Failed to save bank details');
      }
    } catch (err) {
      toast.error('Error saving bank details: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setBankDetailsLoading(false);
    }
  };

  // Prepare trips data from API response
  const trips = useMemo(() => {
    // Use allVehicleTrips if available, otherwise fallback to travelStats recentTrips
    const tripsData = allVehicleTrips.length > 0 ? allVehicleTrips : (travelStats?.recentTrips || []);
    return tripsData.map(trip => ({
      date: trip.date || trip.tripDate || '',
      route: trip.route || (trip.pickupDrop === 'Pickup' ? `${trip.pickupDrop} -> ${trip.location || 'Unknown'}` : `${trip.location || 'Unknown'} -> ${trip.pickupDrop}`),
      km: String(trip.km || trip.kms || trip.distance || 0),
      type: trip.type || trip.tripType || 'Trip',
      status: trip.status || 'Completed'
    }));
  }, [travelStats, allVehicleTrips]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Top Bar */}
      <div style={{ backgroundColor: '#EEF8F3', borderRadius: '8px', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#555', padding: 0 }}><ArrowLeft size={20} /></button>
          <Breadcrumb items={[{ label: 'Vehicles Management', onClick: onBack }, { label: d.driverName }]} />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleDeactivate}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: '1px solid',
              borderRadius: '6px',
              backgroundColor: vehicleData?.status === 'Inactive' ? '#F59E0B' : '#227B48',
              borderColor: vehicleData?.status === 'Inactive' ? '#F59E0B' : '#227B48',
              color: 'white',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {vehicleData?.status === 'Inactive' ? 'Activate' : 'Deactivate'}
          </button>

          {/* Edit Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowEditDropdown(!showEditDropdown)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px solid #D0D5DD', borderRadius: '6px', backgroundColor: 'white', color: '#555', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
            >
              <Edit2 size={14} /> Edit <ChevronDown size={14} />
            </button>

            {showEditDropdown && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: '200px', zIndex: 100, border: '1px solid #E5E7EB' }}>
                <button onClick={() => { setShowEditDropdown(false); setShowVehicleDetailsModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 16px', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#333', borderBottom: '1px solid #F3F4F6' }}>
                  <Car size={14} color="#6B7280" /> Vehicle Profile
                </button>
                <button onClick={async () => {
                  setShowEditDropdown(false);

                  // Get driver data from vehicleDrivers, linkedDriver, or vehicle's drivers array
                  let driverData = null;

                  // First check if we have vehicleDrivers (prioritize multiple drivers)
                  if (vehicleDrivers && vehicleDrivers.length > 0) {
                    driverData = vehicleDrivers[0]; // Use the first driver
                  } else if (linkedDriver) {
                    driverData = linkedDriver;
                  } else if (d.drivers && d.drivers.length > 0) {
                    driverData = d.drivers[0]; // Use the first driver
                  }

                  if (driverData) {
                    const initialForm = {
                      driverName: driverData.driverName || driverData.name || '',
                      contactNumber: driverData.contactNumber || driverData.phone || '',
                      address: driverData.address || '',
                      aadharNumber: driverData.aadharNumber || '',
                      panNumber: driverData.panNumber || '',
                      licenseNumber: driverData.licenseNumber || '',
                      // File uploads
                      aadharFront: null,
                      aadharBack: null,
                      panDoc: null,
                      licenseFront: null,
                      licenseBack: null
                    };
                    setUpdateDriverForm(initialForm);
                    setOriginalDriverForm(initialForm);
                  } else {
                    // If no driver data found, show empty form in edit mode
                    const emptyForm = {
                      driverName: '',
                      contactNumber: '',
                      address: '',
                      aadharNumber: '',
                      panNumber: '',
                      licenseNumber: '',
                      aadharFront: null,
                      aadharBack: null,
                      panDoc: null,
                      licenseFront: null,
                      licenseBack: null
                    };
                    setUpdateDriverForm(emptyForm);
                    setOriginalDriverForm(emptyForm);
                  }
                  setShowUpdateDriverModal(true);
                }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 16px', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#333', borderBottom: '1px solid #F3F4F6' }}>
                  <User size={14} color="#6B7280" /> Update Driver
                </button>
                <button onClick={() => { setShowEditDropdown(false); handleOpenDriverModal(); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 16px', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#333', borderBottom: '1px solid #F3F4F6' }}>
                  <Plus size={14} color="#6B7280" /> Add Driver
                </button>
                <button onClick={() => {
                  setShowEditDropdown(false);
                  // Pre-select currently assigned companies
                  const assignedClientObjects = currentAssignments.map(assignment => {
                    const client = clients.find(c => c.id === (assignment.clientId || assignment.client_id));
                    return client || { id: assignment.clientId || assignment.client_id, companyName: assignment.clientName };
                  }).filter(client => client && client.id);
                  setSelectedClients(assignedClientObjects);
                  setShowChangeCompanyModal(true);
                }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 16px', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#333', borderBottom: '1px solid #F3F4F6' }}>
                  <Building size={14} color="#6B7280" /> Change Assigned Company
                </button>
                <button onClick={() => { setShowEditDropdown(false); setShowUpdateBankModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 16px', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#333' }}>
                  <CreditCard size={14} color="#6B7280" /> {(d?.accountNumber || d?.bankName || d?.ifscCode || d?.branch) ? 'Update Bank Details' : 'Add Bank Details'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Card */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '24px' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '16px', backgroundColor: '#EEF8F3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Car size={36} color="#20B967" />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: 700, color: '#333' }}>{d.vehicleNumber} ({d.vehicleType})</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 24px', marginBottom: '12px' }}>
            {/* Left Column - Owner Info */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555', marginBottom: '6px' }}>
                <User size={16} color="#20B967" />
                <span><strong>Owner:</strong> {d.ownerName || 'Unknown Owner'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
                <Phone size={16} color="#20B967" />
                <span>{d.ownerContactNumber || d.contactNumber || d.phone || 'N/A'}</span>
              </div>
            </div>
            {/* Middle Column - Driver Name & Phone */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555', marginBottom: '6px' }}>
                <User size={16} color="#20B967" />
                <span>
                  <strong>Driver:</strong>{' '}
                  {vehicleDrivers && vehicleDrivers.length > 0
                    ? vehicleDrivers.map(drv => drv.driverName || drv.name).join(', ')
                    : (linkedDriver?.driverName
                      ? linkedDriver.driverName
                      : (d.drivers && d.drivers.length > 0
                        ? d.drivers.map(drv => drv.driverName || drv.name).join(', ')
                        : (d.displayDriverName ||
                          <button
                            onClick={() => setShowVehicleDetailsModal(true)}
                            style={{
                              backgroundColor: '#E8F5EE',
                              color: '#07A95B',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '4px 12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#D1E7DD';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#E8F5EE';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            View Profile
                          </button>)))
                  }
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
                <Phone size={16} color="#20B967" />
                <span>
                  {vehicleDrivers && vehicleDrivers.length > 0
                    ? vehicleDrivers.map(drv => drv.contactNumber || drv.phone || 'N/A').join(', ')
                    : (linkedDriver?.contactNumber || linkedDriver?.phone
                      ? (linkedDriver.contactNumber || linkedDriver.phone)
                      : (d.drivers && d.drivers.length > 0
                        ? d.drivers.map(drv => drv.contactNumber || drv.phone || 'N/A').join(', ')
                        : (d.displayDriverPhone || 'N/A')))
                  }
                </span>
              </div>
            </div>
            {/* Right Column - Assigned Company */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
                <Building size={16} color="#20B967" />
                <span><strong>Assigned to:</strong> {clientNames.length > 0 ? clientNames.join(', ') : (d.clientName || 'N/A')}</span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {d.paymentMode ? (
              <span style={{ padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, backgroundColor: '#F0F4FF', color: '#5B6EF4' }}>{d.paymentMode}</span>
            ) : (
              <button
                onClick={() => setShowVehicleDetailsModal(true)}
                style={{
                  padding: '4px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: '#E8F5EE',
                  color: '#07A95B',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#D1E7DD';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#E8F5EE';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                View Profile
              </button>
            )}
            <span style={{ padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, backgroundColor: '#F5F5F5', color: '#555' }}>Rate: {d.rate || 'N/A'}</span>
          </div>
        </div>
      </div>


      {/* Date Filters */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#4B5563' }}>From:</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #E5E7EB',
              borderRadius: '20px',
              fontSize: '12px',
              backgroundColor: 'white',
              color: '#374151',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#4B5563' }}>To:</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #E5E7EB',
              borderRadius: '20px',
              fontSize: '12px',
              backgroundColor: 'white',
              color: '#374151',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          />
          {(startDate || endDate) ? (
            <button
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: '#EF4444',
                color: 'white',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#6B7280', margin: '0 4px' }}>Or:</span>
              <CustomSelect
                value={selectedMonth}
                onChange={(val) => {
                  setSelectedMonth(val);
                  setSelectedMonthName(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][val]);
                }}
                options={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => ({ value: i, label: m }))}
                width="84px"
                size="sm"
              />
              <CustomSelect
                value={selectedYear}
                onChange={(val) => setSelectedYear(parseInt(val))}
                options={Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - (3 - i)).map(year => ({ value: year, label: String(year) }))}
                width="88px"
                size="sm"
              />
            </div>
          )}
        </div>

        {/* Share Button - Direct Native Share */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={async () => {
              let doc = null;
              let shareData = null;
              let filenamePeriod = '';

              try {
                const monthName = new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'short' });

                // Use the same data source as the UI display
                const tripsData = allVehicleTrips.length > 0 ? allVehicleTrips : (travelStats?.recentTrips || []);
                let detailedTrips = [];

                if (tripsData && tripsData.length > 0) {
                  detailedTrips = tripsData.map(trip => ({
                    tripDate: trip.date || trip.tripDate || trip.createdAt || '',
                    tripTime: trip.tripTime || trip.time || '',
                    // Use the same route logic as the UI
                    route: trip.route || (trip.pickupDrop === 'Pickup' ? `${trip.pickupDrop} -> ${trip.location || 'Unknown'}` : `${trip.location || 'Unknown'} -> ${trip.pickupDrop}`),
                    km: trip.km || trip.kms || trip.distance || 0,
                    amount: trip.amount || 0,
                    // Keep original fields for debugging
                    pickup: trip.pickup || trip.pickupDrop || 'Unknown',
                    drop: trip.drop || trip.to || trip.destination || trip.location || 'Unknown',
                    location: trip.location || 'Unknown',
                    pickupDrop: trip.pickupDrop || 'Unknown'
                  })).filter(trip => trip.tripDate || trip.date);
                }

                const periodLabel = startDate && endDate
                  ? `${startDate} to ${endDate}`
                  : `${monthName} ${selectedYear}`;

                const totalEarnings = detailedTrips.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)
                  || travelStats?.stats?.monthlyStats?.reduce((sum, m) => sum + parseFloat(m.earnings || 0), 0)
                  || 0;

                shareData = {
                  vehicle: d.vehicleNumber,
                  driver: (vehicleDrivers && vehicleDrivers.length > 0
                    ? vehicleDrivers.map(drv => drv.driverName || drv.name).join(', ')
                    : (linkedDriver?.driverName || d.displayDriverName || 'N/A')),
                  client: d.clientName || 'N/A',
                  period: periodLabel,
                  month: monthName,
                  year: selectedYear,
                  kms: travelStats?.stats?.totalKms || 0,
                  trips: travelStats?.stats?.totalTrips || 0,
                  earnings: totalEarnings,
                  avgKm: travelStats?.stats?.averageKms || 0
                };

                doc = new jsPDF();
                const pageWidth = doc.internal.pageSize.getWidth();
                const margin = 15;

                // Header
                doc.setFontSize(18);
                doc.setTextColor(34, 123, 72);
                doc.text('Monthly Trip Report', pageWidth / 2, 20, { align: 'center' });

                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}`, pageWidth / 2, 28, { align: 'center' });

                // Separator line
                doc.setDrawColor(34, 123, 72);
                doc.setLineWidth(0.5);
                doc.line(margin, 32, pageWidth - margin, 32);

                // Vehicle & Driver Info
                doc.setFontSize(11);
                doc.setTextColor(50, 50, 50);
                doc.text('Vehicle & Driver Details', margin, 40);

                doc.setFontSize(10);
                doc.text(`Vehicle Number: ${shareData.vehicle}`, margin, 48);
                doc.text(`Driver: ${shareData.driver}`, margin, 54);
                doc.text(`Assigned to: ${shareData.client}`, margin, 60);
                doc.text(`Period: ${shareData.period}`, margin, 66);

                // Monthly Summary Stats
                doc.setFontSize(11);
                doc.text('Monthly Summary', margin, 78);

                const statsData = [
                  ['Total Trips', `${shareData.trips}`],
                  ['Total KMs', `${shareData.kms.toLocaleString()} KM`],
                  ['Total Amount', `Rs ${parseInt(shareData.earnings).toLocaleString()}`],
                  ['Average KMs per Trip', `${shareData.avgKm} KM`]
                ];

                autoTable(doc, {
                  startY: 82,
                  body: statsData,
                  theme: 'plain',
                  styles: { fontSize: 10, cellPadding: 3 },
                  columnStyles: {
                    0: { fontStyle: 'bold', textColor: 50 },
                    1: { textColor: 80 }
                  },
                  margin: { left: margin, right: margin }
                });

                // Detailed Trip Table
                const finalY = doc.lastAutoTable?.finalY || 110;
                doc.setFontSize(11);
                doc.text('Detailed Trip Information', margin, finalY + 10);

                if (detailedTrips.length > 0) {
                  // Sort trips by date
                  const sortedTrips = [...detailedTrips].sort((a, b) => {
                    const dateA = new Date(a.tripDate || a.date || a.createdAt);
                    const dateB = new Date(b.tripDate || b.date || b.createdAt);
                    return dateA - dateB;
                  });

                  // Calculate rate per km from total earnings and total kms
                  const ratePerKm = shareData.kms > 0 ? (shareData.earnings / shareData.kms) : 0;

                  const tripTableData = sortedTrips.map((trip, index) => {
                    const tripKm = trip.km || trip.kms || trip.distance || 0;
                    const tripAmount = trip.amount || (tripKm * ratePerKm) || 0;
                    const tripDate = trip.tripDate || trip.date || trip.createdAt || '';
                    const tripTime = trip.tripTime || trip.time || '';

                    // Use the route field that's already working in the UI
                    const location = trip.route || 'Unknown';

                    // Format date
                    let formattedDate = 'N/A';
                    let formattedTime = 'N/A';
                    if (tripDate) {
                      const dateObj = new Date(tripDate);
                      formattedDate = dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                      if (tripTime) {
                        formattedTime = tripTime;
                      } else if (!isNaN(dateObj.getTime())) {
                        formattedTime = dateObj.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
                      }
                    }

                    return [
                      (index + 1).toString(),
                      formattedDate,
                      formattedTime,
                      location,
                      `${Number(tripKm).toFixed(1)} KM`,
                      `Rs ${Math.round(tripAmount).toLocaleString()}`
                    ];
                  });

                  autoTable(doc, {
                    startY: finalY + 14,
                    head: [['#', 'Date', 'Time', 'Location', 'Distance', 'Amount']],
                    body: tripTableData,
                    theme: 'striped',
                    headStyles: {
                      fillColor: [34, 123, 72],
                      textColor: 255,
                      fontSize: 9,
                      fontStyle: 'bold'
                    },
                    styles: {
                      fontSize: 8,
                      cellPadding: 2,
                      overflow: 'ellipsize'
                    },
                    columnStyles: {
                      0: { cellWidth: 10, halign: 'center' },
                      1: { cellWidth: 28 },
                      2: { cellWidth: 20, halign: 'center' },
                      3: { cellWidth: 'auto' },
                      4: { cellWidth: 22, halign: 'right' },
                      5: { cellWidth: 22, halign: 'right' }
                    },
                    margin: { left: margin, right: margin }
                  });
                } else {
                  doc.setFontSize(10);
                  doc.setTextColor(150, 150, 150);
                  doc.text('No trip details available for this period.', margin, finalY + 20);
                }

                // Footer
                const pageHeight = doc.internal.pageSize.getHeight();
                doc.setFontSize(9);
                doc.setTextColor(150, 150, 150);
                doc.text('Generated by SST - Vehicle Management System', pageWidth / 2, pageHeight - 10, { align: 'center' });

                filenamePeriod = startDate && endDate
                  ? `${startDate}_to_${endDate}`
                  : `${shareData.month}-${shareData.year}`;

                const pdfBlob = doc.output('blob');
                const pdfFile = new File([pdfBlob], `${shareData.vehicle}-${filenamePeriod}.pdf`, { type: 'application/pdf' });

                if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
                  navigator.share({
                    files: [pdfFile],
                    title: `Monthly Trip Report - ${shareData.vehicle}`,
                    text: `Trip Report for ${shareData.vehicle} - ${shareData.period}\nTotal Trips: ${shareData.trips}\nTotal KMs: ${parseFloat(shareData.kms).toLocaleString()} KM\nTotal Amount: Rs ${parseInt(shareData.earnings).toLocaleString()}`
                  }).catch((err) => {
                    console.error("Web Share failed, falling back to download:", err);
                    doc.save(`${shareData.vehicle}-${filenamePeriod}.pdf`);
                  });
                } else {
                  doc.save(`${shareData.vehicle}-${filenamePeriod}.pdf`);
                  toast.info('PDF downloaded. Web Share API not supported on this browser.');
                }
              } catch (err) {
                console.error("Error in Share handler:", err);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              border: '1px solid #227B48',
              borderRadius: '6px',
              backgroundColor: '#227B48',
              color: 'white',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: '12px' }}>
        {travelStatsLoading ? (
          <div style={{ flex: 1, textAlign: 'center', padding: '20px', color: '#888' }}>Loading stats...</div>
        ) : travelStatsError ? (
          <div style={{ flex: 1, textAlign: 'center', padding: '20px', color: '#DC2626' }}>{travelStatsError}</div>
        ) : (
          (() => {
            // Get selected month data from multiMonthStats
            const selectedMonthData = multiMonthStats?.find(m => m.month === selectedMonthName);
            const monthKms = selectedMonthData?.totalKms || 0;

            // Use API totalTrips for current month, trips.length for other months
            // API month is 1-based (Jan=1), JS month is 0-based (Jan=0)
            const isCurrentMonth = (selectedMonth + 1) === travelStats?.stats?.filterApplied?.month &&
              selectedYear === travelStats?.stats?.filterApplied?.year;
            const monthTrips = isCurrentMonth
              ? (travelStats?.stats?.totalTrips || 0)
              : 0;

            // Use API earnings when available
            const earnings = isCurrentMonth
              ? (travelStats?.stats?.monthlyStats?.[0]?.earnings || 0)
              : 0;

            const avgKm = monthTrips > 0 ? Math.round(monthKms / monthTrips) : 0;

            return [
              { label: `KMs in ${selectedMonthName || new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'short' })}`, value: monthKms?.toLocaleString() || '0' },
              { label: 'Total Trips', value: monthTrips?.toString() || '0' },
              { label: `Total Earned (${selectedMonthName || new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'short' })})`, value: `₹${earnings?.toLocaleString() || '0'}` },
              { label: 'Avg KM/Trip', value: avgKm?.toString() || '0' },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, backgroundColor: 'white', borderRadius: '8px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#07A95B' }}>{s.value}</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>{s.label}</div>
              </div>
            ));
          })()
        )}
      </div>

      <div style={{ display: 'flex', gap: '16px' }}>
        {/* KM Chart */}
        <div style={{
          flex: 1,
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '300px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#333' }}>Monthly KM Trend</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  // Shift window to show previous 6 months
                  if (chartWindowStart > 0) {
                    const newStart = Math.max(0, chartWindowStart - 6);
                    setChartWindowStart(newStart);
                    // Select the last month of the new window
                    const newWindowData = multiMonthStats.slice(newStart, newStart + 6);
                    if (newWindowData.length > 0) {
                      const lastMonth = newWindowData[newWindowData.length - 1];
                      setSelectedMonthName(lastMonth.month);
                      setSelectedMonth(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(lastMonth.month));
                      setSelectedYear(lastMonth.year);
                    }
                  }
                }}
                disabled={!multiMonthStats || chartWindowStart <= 0}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: chartWindowStart <= 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: (!multiMonthStats || chartWindowStart <= 0) ? 0.5 : 1
                }}
              >
                <ChevronLeft size={16} color="#666" />
              </button>
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#333', minWidth: '100px', textAlign: 'center' }}>
                {multiMonthStats ? (() => {
                  const windowData = multiMonthStats.slice(chartWindowStart, chartWindowStart + 6);
                  if (windowData.length === 0) return '';
                  const firstMonth = windowData[0];
                  const lastMonth = windowData[windowData.length - 1];
                  if (firstMonth.year === lastMonth.year) {
                    return `${firstMonth.month} - ${lastMonth.month} ${firstMonth.year}`;
                  }
                  return `${firstMonth.month} ${firstMonth.year} - ${lastMonth.month} ${lastMonth.year}`;
                })() : ''}
              </span>
              <button
                onClick={() => {
                  // Shift window to show next 6 months
                  if (chartWindowStart + 6 < multiMonthStats?.length) {
                    const newStart = Math.min(multiMonthStats.length - 6, chartWindowStart + 6);
                    setChartWindowStart(newStart);
                    // Select the first month of the new window
                    const newWindowData = multiMonthStats.slice(newStart, newStart + 6);
                    if (newWindowData.length > 0) {
                      const firstMonth = newWindowData[0];
                      setSelectedMonthName(firstMonth.month);
                      setSelectedMonth(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(firstMonth.month));
                      setSelectedYear(firstMonth.year);
                    }
                  }
                }}
                disabled={!multiMonthStats || chartWindowStart + 6 >= multiMonthStats?.length}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: (chartWindowStart + 6 >= multiMonthStats?.length) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: (!multiMonthStats || chartWindowStart + 6 >= multiMonthStats?.length) ? 0.5 : 1
                }}
              >
                <ChevronRight size={16} color="#666" />
              </button>
            </div>
          </div>
          <div style={{
            flex: 1,
            minHeight: '200px',
            border: 'none',
            outline: 'none',
            boxShadow: 'none'
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={multiMonthStats?.slice(chartWindowStart, chartWindowStart + 6) || []}
                style={{
                  outline: 'none',
                  border: 'none',
                  boxShadow: 'none'
                }}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    const monthName = data.activeLabel;
                    setSelectedMonthName(monthName);
                    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthName);
                    if (monthIndex !== -1) {
                      setSelectedMonth(monthIndex);
                    }
                  }
                }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#888' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Bar
                  dataKey="totalKms"
                  fill="#20B967"
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  stroke="#20B967"
                  strokeWidth={0}
                >
                  {multiMonthStats?.slice(chartWindowStart, chartWindowStart + 6).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.month === selectedMonthName ? '#07A95B' : '#20B967'}
                      onClick={() => {
                        setSelectedMonthName(entry.month);
                        setSelectedYear(entry.year);
                        const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(entry.month);
                        if (monthIndex !== -1) {
                          setSelectedMonth(monthIndex);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Trip History */}
        <div style={{ flex: 1.2, backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: '#333' }}>
            {selectedMonthName ? `${selectedMonthName} Trips` : 'Recent Trips'}
          </h3>
          {tripsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888', fontSize: '14px' }}>
              Loading trips...
            </div>
          ) : trips.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888', fontSize: '14px' }}>
              No trips available
            </div>
          ) : (
            <div style={{ position: 'relative', maxHeight: '300px' }}>
              <div
                id={`trips-slider-${d.vehicleNumber}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  gap: '8px',
                  padding: '4px 0',
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'thin',
                  maxHeight: '280px'
                }}>
                {trips.map((t, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    borderBottom: '1px solid #F5F5F5',
                    gap: '12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #F0F0F0'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '32px',
                      borderRadius: '6px',
                      backgroundColor: '#EEF8F3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#20B967',
                      whiteSpace: 'nowrap'
                    }}>
                      {t.km}km
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.route}</div>
                      <div style={{ fontSize: '12px', color: '#AAA', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.date} · {t.type}</div>
                    </div>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: '#EEF8F3',
                      color: '#20B967',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}>
                      {t.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Driver Assignment Modal */}
      {showDriverModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: '#227B48',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '20px' }}>
                Add New Driver
              </h2>
              <button
                onClick={() => {
                  setShowDriverModal(false);
                  setShowAddDriverForm(false);
                }}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <form onSubmit={handleAddNewDriver}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                      Driver Name *
                    </label>
                    <input
                      type="text"
                      value={driverForm.driverName}
                      onChange={(e) => setDriverForm({ ...driverForm, driverName: e.target.value })}
                      placeholder="Enter driver name"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                      Contact Number *
                    </label>
                    <input
                      type="text"
                      value={driverForm.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                        setDriverForm({ ...driverForm, contactNumber: value });
                      }}
                      placeholder="Enter contact number"
                      required
                      maxLength={10}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                      Address
                    </label>
                    <input
                      type="text"
                      value={driverForm.address}
                      onChange={(e) => setDriverForm({ ...driverForm, address: e.target.value })}
                      placeholder="Enter address"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                      Aadhar Number
                    </label>
                    <input
                      type="text"
                      value={driverForm.aadharNumber}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, '');
                        // Auto-format: 1234-1234-1234 (12 digits)
                        if (value.length > 4) value = value.slice(0, 4) + '-' + value.slice(4);
                        if (value.length > 9) value = value.slice(0, 9) + '-' + value.slice(9);
                        if (value.length > 14) value = value.slice(0, 14);
                        setDriverForm({ ...driverForm, aadharNumber: value });
                      }}
                      placeholder="1234-1234-1234"
                      maxLength={14}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        letterSpacing: '0.5px'
                      }}
                    />
                    <div style={{ marginTop: '12px', display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                          Aadhar Front
                        </label>
                        <label style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '16px 12px',
                          border: `2px dashed ${driverForm.aadharFront ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px',
                          backgroundColor: driverForm.aadharFront ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          minHeight: '80px'
                        }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setDriverForm({ ...driverForm, aadharFront: e.target.files[0] })}
                            style={{ display: 'none' }}
                          />
                          {driverForm.aadharFront ? (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span>
                              <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.aadharFront.name}</span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span>
                            </>
                          )}
                        </label>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                          Aadhar Back
                        </label>
                        <label style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '16px 12px',
                          border: `2px dashed ${driverForm.aadharBack ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px',
                          backgroundColor: driverForm.aadharBack ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          minHeight: '80px'
                        }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setDriverForm({ ...driverForm, aadharBack: e.target.files[0] })}
                            style={{ display: 'none' }}
                          />
                          {driverForm.aadharBack ? (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span>
                              <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.aadharBack.name}</span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                      PAN Number
                    </label>
                    <input
                      type="text"
                      value={driverForm.panNumber}
                      onChange={(e) => setDriverForm({ ...driverForm, panNumber: e.target.value.toUpperCase() })}
                      placeholder="Enter PAN number"
                      maxLength={10}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                        PAN Card Document
                      </label>
                      <label style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        border: `2px dashed ${driverForm.panDoc ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px',
                        backgroundColor: driverForm.panDoc ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setDriverForm({ ...driverForm, panDoc: e.target.files[0] })}
                          style={{ display: 'none' }}
                        />
                        {driverForm.panDoc ? (
                          <>
                            <span style={{ fontSize: '32px', marginBottom: '8px' }}>✅</span>
                            <span style={{ fontSize: '14px', color: '#22C55E', fontWeight: 500 }}>{driverForm.panDoc.name}</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: '32px', marginBottom: '8px' }}>📄</span>
                            <span style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>Click to upload PAN document</span>
                            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>PDF, JPG, PNG</span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                      License Number
                    </label>
                    <input
                      type="text"
                      value={driverForm.licenseNumber}
                      onChange={(e) => {
                        let value = e.target.value.toUpperCase();
                        // Auto-format: XX-NN-NNNN-NNNNNNN
                        value = value.replace(/[^A-Z0-9]/g, '');
                        if (value.length > 2) value = value.slice(0, 2) + '-' + value.slice(2);
                        if (value.length > 5) value = value.slice(0, 5) + '-' + value.slice(5);
                        if (value.length > 10) value = value.slice(0, 10) + '-' + value.slice(10);
                        if (value.length > 18) value = value.slice(0, 18);
                        setDriverForm({ ...driverForm, licenseNumber: value });
                      }}
                      placeholder="AA-12-1234-12345678"
                      maxLength={18}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        letterSpacing: '0.5px'
                      }}
                    />
                    <div style={{ marginTop: '12px', display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                          Licence Front
                        </label>
                        <label style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '16px 12px',
                          border: `2px dashed ${driverForm.licenseFront ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px',
                          backgroundColor: driverForm.licenseFront ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          minHeight: '80px'
                        }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setDriverForm({ ...driverForm, licenseFront: e.target.files[0] })}
                            style={{ display: 'none' }}
                          />
                          {driverForm.licenseFront ? (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span>
                              <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.licenseFront.name}</span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span>
                            </>
                          )}
                        </label>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                          Licence Back
                        </label>
                        <label style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '16px 12px',
                          border: `2px dashed ${driverForm.licenseBack ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px',
                          backgroundColor: driverForm.licenseBack ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          minHeight: '80px'
                        }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => setDriverForm({ ...driverForm, licenseBack: e.target.files[0] })}
                            style={{ display: 'none' }}
                          />
                          {driverForm.licenseBack ? (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span>
                              <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.licenseBack.name}</span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>Mandatory Onboarding Documents</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      {/* PVC Document */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Police Verification (PVC)</label>
                        <label style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '16px 12px', border: `2px dashed ${driverForm.pvcDocument ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px', backgroundColor: driverForm.pvcDocument ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer', transition: 'all 0.2s', minHeight: '80px'
                        }}>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDriverForm({ ...driverForm, pvcDocument: e.target.files[0] })} style={{ display: 'none' }} />
                          {driverForm.pvcDocument ? (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span><span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.pvcDocument.name}</span></>
                          ) : (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span><span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span></>
                          )}
                        </label>
                      </div>
                      {/* BGV Document */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Background Verification (BGV)</label>
                        <label style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '16px 12px', border: `2px dashed ${driverForm.bgvDocument ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px', backgroundColor: driverForm.bgvDocument ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer', transition: 'all 0.2s', minHeight: '80px'
                        }}>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDriverForm({ ...driverForm, bgvDocument: e.target.files[0] })} style={{ display: 'none' }} />
                          {driverForm.bgvDocument ? (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span><span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.bgvDocument.name}</span></>
                          ) : (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span><span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span></>
                          )}
                        </label>
                      </div>
                      {/* Passport */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Passport</label>
                        <label style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '16px 12px', border: `2px dashed ${driverForm.passport ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px', backgroundColor: driverForm.passport ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer', transition: 'all 0.2s', minHeight: '80px'
                        }}>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDriverForm({ ...driverForm, passport: e.target.files[0] })} style={{ display: 'none' }} />
                          {driverForm.passport ? (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span><span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.passport.name}</span></>
                          ) : (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span><span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span></>
                          )}
                        </label>
                      </div>
                      {/* Medical Certificate */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Medical Certificate</label>
                        <label style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '16px 12px', border: `2px dashed ${driverForm.medicalCertificate ? '#22C55E' : '#D1D5DB'}`,
                          borderRadius: '8px', backgroundColor: driverForm.medicalCertificate ? '#F0FDF4' : '#F9FAFB',
                          cursor: 'pointer', transition: 'all 0.2s', minHeight: '80px'
                        }}>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDriverForm({ ...driverForm, medicalCertificate: e.target.files[0] })} style={{ display: 'none' }} />
                          {driverForm.medicalCertificate ? (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>✅</span><span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 500, textAlign: 'center', wordBreak: 'break-all' }}>{driverForm.medicalCertificate.name}</span></>
                          ) : (
                            <><span style={{ fontSize: '24px', marginBottom: '4px' }}>📄</span><span style={{ fontSize: '12px', color: '#6B7280' }}>Click to upload</span></>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setShowDriverModal(false)}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#227B48',
                      color: 'white',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Adding...' : 'Add Driver'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update Vehicle Modal */}
      {showUpdateVehicleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: '#227B48',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '20px' }}>Vehicle Profile</h2>
              <button
                onClick={() => setShowUpdateVehicleModal(false)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdateVehicle} style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Vehicle Number</label>
                  <input
                    type="text"
                    value={vehicleForm.vehicleNumber}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, vehicleNumber: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Vehicle Type</label>
                  <CustomSelect
                    value={vehicleForm.vehicleType}
                    onChange={(val) => setVehicleForm({ ...vehicleForm, vehicleType: val })}
                    options={VEHICLE_TYPES}
                    placeholder="Select Vehicle Type"
                    width="100%"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Owner Name</label>
                  <input
                    type="text"
                    value={vehicleForm.ownerName}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, ownerName: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Contact Number</label>
                  <input
                    type="text"
                    value={vehicleForm.contactNumber}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, contactNumber: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setShowUpdateVehicleModal(false)}
                  style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#227B48', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Driver Modal */}
      {showUpdateDriverModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: '#227B48',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '20px' }}>Update Driver</h2>
              <button
                onClick={() => setShowUpdateDriverModal(false)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdateDriver} style={{ padding: '24px', maxHeight: '70vh', overflow: 'auto' }}>
              <div style={{ display: 'grid', gap: '16px' }}>
                {/* Driver Name Field */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 500 }}>Driver Name *</label>
                  </div>
                  <input
                    type="text"
                    value={updateDriverForm.driverName}
                    onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, driverName: e.target.value })}
                    placeholder="Enter driver name"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>

                {/* Contact Number Field */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 500 }}>Contact Number *</label>
                  </div>
                  <input
                    type="text"
                    value={updateDriverForm.contactNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                      setUpdateDriverForm({ ...updateDriverForm, contactNumber: value });
                    }}
                    placeholder="Enter 10-digit contact number"
                    maxLength={10}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>

                {/* Address Field */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 500 }}>Address</label>
                  </div>
                  <input
                    type="text"
                    value={updateDriverForm.address}
                    onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, address: e.target.value })}
                    placeholder="Enter address"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>

                {/* Aadhar Number Field */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 500 }}>Aadhar Number</label>
                  </div>
                  <input
                    type="text"
                    value={updateDriverForm.aadharNumber}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9]/g, '');
                      if (value.length > 4) value = value.slice(0, 4) + '-' + value.slice(4);
                      if (value.length > 9) value = value.slice(0, 9) + '-' + value.slice(9);
                      if (value.length > 14) value = value.slice(0, 14);
                      setUpdateDriverForm({ ...updateDriverForm, aadharNumber: value });
                    }}
                    placeholder="1234-1234-1234"
                    maxLength={14}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', fontFamily: 'monospace', letterSpacing: '0.5px' }}
                  />
                </div>

                {/* PAN Number Field */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 500 }}>PAN Number</label>
                  </div>
                  <input
                    type="text"
                    value={updateDriverForm.panNumber}
                    onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, panNumber: e.target.value.toUpperCase() })}
                    placeholder="Enter PAN number"
                    maxLength={10}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>

                {/* License Number Field */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: 500 }}>License Number</label>
                  </div>
                  <input
                    type="text"
                    value={updateDriverForm.licenseNumber}
                    onChange={(e) => {
                      let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      if (value.length > 2) value = value.slice(0, 2) + '-' + value.slice(2);
                      if (value.length > 5) value = value.slice(0, 5) + '-' + value.slice(5);
                      if (value.length > 10) value = value.slice(0, 10) + '-' + value.slice(10);
                      if (value.length > 18) value = value.slice(0, 18);
                      setUpdateDriverForm({ ...updateDriverForm, licenseNumber: value });
                    }}
                    placeholder="AA-12-1234-1234567"
                    maxLength={18}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px', fontFamily: 'monospace', letterSpacing: '0.5px' }}
                  />
                </div>

                {/* Document Uploads */}
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>Driver Documents</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    {/* Aadhar Front */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Aadhar Front</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.aadharFront ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.aadharFront ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, aadharFront: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.aadharFront ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.aadharFront.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* Aadhar Back */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Aadhar Back</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.aadharBack ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.aadharBack ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, aadharBack: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.aadharBack ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.aadharBack.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* PAN Card */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>PAN Card</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.panDoc ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.panDoc ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, panDoc: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.panDoc ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.panDoc.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* License Front */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>License Front</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.licenseFront ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.licenseFront ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, licenseFront: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.licenseFront ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.licenseFront.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* License Back */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>License Back</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.licenseBack ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.licenseBack ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, licenseBack: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.licenseBack ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.licenseBack.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* PVC Document */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Police Verification (PVC)</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.pvcDocument ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.pvcDocument ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, pvcDocument: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.pvcDocument ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.pvcDocument.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* BGV Document */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Background Verification (BGV)</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.bgvDocument ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.bgvDocument ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, bgvDocument: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.bgvDocument ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.bgvDocument.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* Passport */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Passport</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.passport ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.passport ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, passport: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.passport ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.passport.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                    {/* Medical Certificate */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>Medical Certificate</label>
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '12px', border: `2px dashed ${updateDriverForm.medicalCertificate ? '#22C55E' : '#D1D5DB'}`,
                        borderRadius: '8px', backgroundColor: updateDriverForm.medicalCertificate ? '#F0FDF4' : '#F9FAFB',
                        cursor: 'pointer', transition: 'all 0.2s', minHeight: '60px'
                      }}>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdateDriverForm({ ...updateDriverForm, medicalCertificate: e.target.files[0] })} style={{ display: 'none' }} />
                        {updateDriverForm.medicalCertificate ? (
                          <><span style={{ fontSize: '20px' }}>✅</span><span style={{ fontSize: '11px', color: '#22C55E', fontWeight: 500, textAlign: 'center' }}>{updateDriverForm.medicalCertificate.name}</span></>
                        ) : (
                          <><span style={{ fontSize: '20px' }}>📄</span><span style={{ fontSize: '11px', color: '#6B7280' }}>Click to upload</span></>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setShowUpdateDriverModal(false)}
                  style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#227B48', color: 'white', cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Assigned Company Modal */}
      {showChangeCompanyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
              Update Vehicle Assignment
            </h3>
            <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
              Vehicle: <strong>{d.vehicleNumber || 'N/A'}</strong>
            </p>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '6px', fontSize: '13px', color: '#92400E' }}>
              <strong>Currently assigned to:</strong>
              {currentAssignments.length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {currentAssignments.map((assignment, index) => (
                    <div key={assignment.id || index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      backgroundColor: '#FFF',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      border: '1px solid #FCD34D'
                    }}>
                      <span style={{ color: '#92400E', fontWeight: 500 }}>
                        {assignment.clientName || 'Unknown Client'}
                      </span>
                      <button
                        onClick={async () => {
                          const confirmed = await showConfirm({
                            title: 'Unassign Company',
                            message: `Are you sure you want to unassign this vehicle from ${assignment.clientName || 'this company'}?`,
                            danger: true,
                            confirmLabel: 'Unassign',
                            cancelLabel: 'Cancel',
                          });
                          if (!confirmed) return;
                          try {
                            const response = await vehicalsManagementApi.deleteVehicleClientAssignment(assignment.id);
                            if (response.success) {
                              toast.success('Company unassigned successfully!');
                              await refreshData();
                              setShowChangeCompanyModal(false);
                              setSelectedClients([]);
                            } else {
                              toast.error('Failed to unassign company. Please try again.');
                            }
                          } catch (err) {
                            toast.error('Error unassigning company: ' + (err.response?.data?.message || err.message || 'Unknown error'));
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#DC2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#B91C1C'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#DC2626'}
                      >
                        Unassign
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <span style={{ marginTop: '4px', display: 'block' }}>N/A</span>
              )}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#333' }}>Select Clients (Multiple selection allowed)</label>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '6px',
                padding: '8px',
                backgroundColor: 'white',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {clients.map(client => (
                  <div key={client.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 4px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: selectedClients.some(sc => sc.id === client.id) ? '#E8F5E8' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                    onClick={() => {
                      const isSelected = selectedClients.some(sc => sc.id === client.id);
                      if (isSelected) {
                        setSelectedClients(selectedClients.filter(sc => sc.id !== client.id));
                      } else {
                        setSelectedClients([...selectedClients, client]);
                      }
                    }}
                    onMouseEnter={e => {
                      if (!selectedClients.some(sc => sc.id === client.id)) {
                        e.currentTarget.style.backgroundColor = '#F5F5F5';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!selectedClients.some(sc => sc.id === client.id)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}>
                    <input
                      type="checkbox"
                      checked={selectedClients.some(sc => sc.id === client.id)}
                      onChange={() => { }}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#333' }}>{client.companyName}</span>
                  </div>
                ))}
              </div>
              {selectedClients.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Selected: {selectedClients.map(c => c.companyName).join(', ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => { setShowChangeCompanyModal(false); setSelectedClients([]); }}
                style={{ padding: '10px 20px', border: '1px solid #ddd', backgroundColor: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: '#333' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const confirmed = await showConfirm({
                    title: 'Unassign All Clients',
                    message: 'Are you sure you want to unassign this vehicle from all clients?',
                    danger: true,
                    confirmLabel: 'Unassign All',
                    cancelLabel: 'Cancel',
                  });
                  if (!confirmed) return;
                  try {
                    setLoading(true);

                    // Delete all assignments for this vehicle
                    let allSuccessful = true;
                    for (const assignment of currentAssignments) {
                      const response = await vehicalsManagementApi.deleteVehicleClientAssignment(assignment.id);
                      if (!response.success) {
                        allSuccessful = false;
                      }
                    }

                    if (allSuccessful) {
                      toast.success('Vehicle unassigned successfully!');
                      setShowChangeCompanyModal(false);
                      setSelectedClients([]);
                      await refreshData();
                    } else {
                      toast.error('Some assignments could not be removed. Please try again.');
                    }
                  } catch (err) {
                    toast.error('Error unassigning vehicle: ' + (err.response?.data?.message || err.message || 'Unknown error'));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || currentAssignments.length === 0}
                style={{ padding: '10px 20px', border: 'none', backgroundColor: '#F97316', color: 'white', borderRadius: '6px', cursor: (loading || currentAssignments.length === 0) ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: (loading || currentAssignments.length === 0) ? 0.6 : 1 }}
              >
                {loading ? 'Processing...' : 'Unassign'}
              </button>
              <button
                onClick={handleChangeCompany}
                disabled={loading || selectedClients.length === 0}
                style={{ padding: '10px 20px', border: 'none', backgroundColor: '#22C55E', color: 'white', borderRadius: '6px', cursor: (loading || selectedClients.length === 0) ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: (loading || selectedClients.length === 0) ? 0.6 : 1 }}
              >
                {loading ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Bank Details Modal */}
      {showUpdateBankModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              backgroundColor: '#227B48',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: 'white', fontSize: '20px' }}>Update Bank Details</h2>
              <button
                onClick={() => setShowUpdateBankModal(false)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdateBankDetails} style={{ padding: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    Account Holder Name *
                  </label>
                  <input
                    type="text"
                    value={bankDetailsForm.accountHolderName}
                    onChange={(e) => setBankDetailsForm(prev => ({ ...prev, accountHolderName: e.target.value }))}
                    placeholder="Enter account holder name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    Account Number *
                  </label>
                  <input
                    type="text"
                    value={bankDetailsForm.accountNumber}
                    onChange={(e) => setBankDetailsForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                    placeholder="Enter account number"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    value={bankDetailsForm.bankName}
                    onChange={(e) => setBankDetailsForm(prev => ({ ...prev, bankName: e.target.value }))}
                    placeholder="Enter bank name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    Bank Category *
                  </label>
                  <CustomSelect
                    value={bankDetailsForm.bankCategory || "APW"}
                    onChange={(val) => setBankDetailsForm(prev => ({ ...prev, bankCategory: val }))}
                    options={[
                      { value: 'APW', label: 'APW' },
                      { value: 'APO', label: 'APO' },
                      { value: 'Other', label: 'Other' },
                    ]}
                    width="100%"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    IFSC Code *
                  </label>
                  <input
                    type="text"
                    value={bankDetailsForm.ifscCode}
                    onChange={(e) => setBankDetailsForm(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                    placeholder="Enter IFSC code (e.g., SBIN0001234)"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textTransform: 'uppercase'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                    Branch *
                  </label>
                  <input
                    type="text"
                    value={bankDetailsForm.branch}
                    onChange={(e) => setBankDetailsForm(prev => ({ ...prev, branch: e.target.value }))}
                    placeholder="Enter branch name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setShowUpdateBankModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bankDetailsLoading}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: bankDetailsLoading ? '#9CA3AF' : '#227B48',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: bankDetailsLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {bankDetailsLoading ? 'Saving...' : (existingBankDetails ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vehicle Details Modal */}
      <VehicleDetailsModal
        isOpen={showVehicleDetailsModal}
        onClose={() => setShowVehicleDetailsModal(false)}
        vehicle={d}
      />
    </div>
  );
};

export default DriverDetail;
